import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { useSharedPassword, getSharedPassword } from '../../services/shareService';
import { Button } from '../UI';
import './SharedPassword.css';

/**
 * 공유된 패스워드 조회 페이지
 * URL: /shared/:shareKey
 * 인증 불필요 - 공유 링크로 직접 접근 가능
 */
function SharedPassword() {
  const { shareKey } = useParams();
  const navigate = useNavigate();
  
  const [passwordData, setPasswordData] = useState(null);
  const [shareInfo, setShareInfo] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [showPassword, setShowPassword] = useState(false);
  const [copyFeedback, setCopyFeedback] = useState({});

  // 🔒 전역 중복 실행 방지 (React Strict Mode 대응)
  const processedKey = `processed_${shareKey}`;
  
  // 공유된 패스워드 조회 및 사용 횟수 차감 (단 1회만 실행)
  useEffect(() => {
    // 🔒 이미 처리되었는지 sessionStorage에서 확인
    if (sessionStorage.getItem(processedKey)) {
      console.log('🔒 이미 처리된 요청이므로 건너뜀 (sessionStorage 확인)');
      setLoading(false);
      return;
    }

    const fetchSharedPassword = async () => {
      try {
        setLoading(true);
        setError(null);
        
        // 🔒 처리 시작 즉시 세션에 기록 (중복 방지)
        sessionStorage.setItem(processedKey, new Date().toISOString());
        
        console.log('🔍 공유 링크 접근 시작:', { shareKey });
        console.log('🔍 현재 환경:', {
          origin: window.location.origin,
          pathname: window.location.pathname,
          userAgent: navigator.userAgent.substring(0, 50) + '...'
        });
        
        if (!shareKey) {
          throw new Error('유효하지 않은 공유 링크입니다.');
        }
        
        // 접근자 정보 수집
        const accessorInfo = {
          userAgent: navigator.userAgent,
          timestamp: new Date().toISOString(),
          referrer: document.referrer || '',
          language: navigator.language || '',
          platform: navigator.platform || '',
          origin: window.location.origin
        };
        
        console.log('🔍 접근자 정보:', accessorInfo);
        
        // 🔥 실제 사용 횟수 차감하는 함수 호출
        console.log('🔍 useSharedPassword 함수 호출 시작...');
        console.log('🔍 shareKey:', shareKey);
        
        try {
          const result = await useSharedPassword(shareKey, 'anonymous', accessorInfo);
          console.log('✅ useSharedPassword 함수 호출 성공:', result);
          
          setPasswordData(result.passwordData);
          setShareInfo(result.shareInfo);
          
          console.log('✅ 공유 링크 사용 완료 (회수 차감됨):', {
            remainingUses: result.shareInfo.remainingUses,
            currentUses: result.shareInfo.currentUses,
            isActive: result.shareInfo.isActive
          });
        } catch (useError) {
          console.warn('⚠️ useSharedPassword 실패:', useError);
          console.warn('⚠️ 에러 메시지:', useError.message);
          console.warn('⚠️ 에러 코드:', useError.code);
          
          // Firebase 권한 에러인 경우 특별 처리
          if (useError.message?.includes('permission-denied') || useError.code === 'permission-denied') {
            console.log('🔄 권한 문제로 인한 fallback 시도...');
            
            try {
              // Fallback: 회수 차감 없이 조회만 시도
              const fallbackResult = await getSharedPassword(shareKey, 'anonymous');
              console.log('✅ fallback 성공 (회수 차감 안됨):', fallbackResult);
              
              setPasswordData(fallbackResult.passwordData);
              setShareInfo({
                ...fallbackResult.shareInfo,
                fallbackMode: true // fallback 모드 표시용
              });
              
              console.log('⚠️ Fallback 모드: 공유 링크 조회만 성공, 회수는 차감되지 않음');
            } catch (fallbackError) {
              console.error('❌ Fallback도 실패:', fallbackError);
              throw fallbackError;
            }
          } else {
            // 권한 문제가 아닌 다른 에러는 그대로 throw
            throw useError;
          }
        }
        
      } catch (err) {
        console.error('❌ 공유 패스워드 조회 실패:', err);
        console.error('❌ 에러 상세:', {
          message: err.message,
          stack: err.stack,
          name: err.name,
          code: err.code
        });
        
        // Firebase 에러 코드별 한국어 메시지
        let errorMessage = err.message;
        if (err.code) {
          switch (err.code) {
            case 'permission-denied':
              errorMessage = '접근 권한이 없습니다. Firebase 보안 규칙을 확인해주세요.';
              break;
            case 'unavailable':
              errorMessage = 'Firebase 서비스에 연결할 수 없습니다. 네트워크를 확인해주세요.';
              break;
            case 'not-found':
              errorMessage = '공유 링크를 찾을 수 없습니다.';
              break;
            default:
              errorMessage = `Firebase 에러 (${err.code}): ${err.message}`;
          }
        }
        
        setError(errorMessage);
      } finally {
        setLoading(false);
      }
    };
    
    // 비동기 함수 실행
    console.log('🔍 fetchSharedPassword 실행 시작...');
    fetchSharedPassword();
    
    // 🧹 컴포넌트 언마운트 시 세션 정리
    return () => {
      // 페이지를 벗어날 때 세션 정리하지 않음 (재방문 방지를 위해)
    };
  }, [shareKey, processedKey]); // shareKey만 의존성으로 사용

  // 안전한 창 닫기 함수
  const handleCloseWindow = () => {
    try {
      // 스크립트로 열린 창인지 확인
      if (window.opener) {
        window.close();
      } else {
        // 직접 접근한 경우: 이전 페이지로 이동 또는 홈으로 이동
        if (window.history.length > 1) {
          window.history.back();
        } else {
          // 히스토리가 없으면 탭을 닫도록 안내
          alert('이 탭을 수동으로 닫아주세요. 브라우저 보안 정책으로 인해 자동으로 닫을 수 없습니다.');
        }
      }
    } catch (error) {
      console.log('창 닫기 시도 실패:', error.message);
      // 실패해도 사용자에게는 알리지 않음 (UX 고려)
      alert('이 탭을 수동으로 닫아주세요.');
    }
  };

  // 클립보드 복사 함수
  const copyToClipboard = async (text, field) => {
    try {
      await navigator.clipboard.writeText(text);
      setCopyFeedback(prev => ({ ...prev, [field]: true }));
      setTimeout(() => {
        setCopyFeedback(prev => ({ ...prev, [field]: false }));
      }, 2000);
    } catch (err) {
      console.error('클립보드 복사 실패:', err);
      alert('클립보드 복사에 실패했습니다.');
    }
  };

  // 패스워드 보기/숨기기 토글
  const togglePasswordVisibility = () => {
    setShowPassword(!showPassword);
  };

  // 로딩 상태
  if (loading) {
    return (
      <div className="shared-password-container">
        <div className="shared-password-card">
          <div className="loading-state">
            <div className="loading-spinner"></div>
            <h3>🔐 공유 링크 확인 중...</h3>
            <p>잠시만 기다려주세요.</p>
          </div>
        </div>
      </div>
    );
  }

  // 에러 상태
  if (error) {
    return (
      <div className="shared-password-container">
        <div className="shared-password-card error">
          <div className="error-content">
            <div className="error-icon">⚠️</div>
            <h3>접근할 수 없는 링크</h3>
            <p className="error-message">{error}</p>
            <div className="error-details">
              <h4>가능한 원인:</h4>
              <ul>
                <li>링크가 만료되었습니다</li>
                <li>최대 사용 횟수를 초과했습니다</li>
                <li>링크가 취소되었습니다</li>
                <li>잘못된 링크입니다</li>
              </ul>
            </div>
            <Button
              variant="primary"
              size="md"
              onClick={() => navigate('/')}
              style={{ marginTop: '20px' }}
            >
              홈페이지로 이동
            </Button>
          </div>
        </div>
      </div>
    );
  }

  // 성공 상태 - 패스워드 정보 표시
  return (
    <div className="shared-password-container">
      <div className="shared-password-card">
        {/* 헤더 */}
        <div className="shared-password-header">
          <h2>🔗 공유된 패스워드</h2>
          <p className="share-description">
            안전하게 공유된 패스워드 정보입니다. 필요한 정보를 복사한 후 이 페이지를 닫으세요.
          </p>
          
          {/* Fallback 모드 경고 */}
          {shareInfo?.fallbackMode && (
            <div style={{
              backgroundColor: '#fff3cd',
              color: '#856404',
              padding: '12px',
              borderRadius: '6px',
              border: '1px solid #ffeaa7',
              marginTop: '12px'
            }}>
              <strong>⚠️ 알림:</strong> 공유 링크에 접근했지만 사용 횟수가 차감되지 않았습니다. 
              네트워크나 권한 문제일 수 있습니다.
            </div>
          )}
        </div>

        {/* 패스워드 정보 */}
        <div className="password-info-section">
          <h3>📋 패스워드 정보</h3>
          
          {/* 제목 */}
          <div className="info-row">
            <label>제목</label>
            <div className="info-content">
              <span className="info-value">{passwordData?.title || '-'}</span>
              <Button
                variant="outline"
                size="sm"
                onClick={() => copyToClipboard(passwordData?.title || '', 'title')}
                disabled={!passwordData?.title}
                className="copy-btn"
              >
                {copyFeedback.title ? '✅ 복사됨' : '📋 복사'}
              </Button>
            </div>
          </div>

          {/* 사용자명 */}
          <div className="info-row">
            <label>사용자명</label>
            <div className="info-content">
              <span className="info-value">{passwordData?.username || '-'}</span>
              <Button
                variant="outline"
                size="sm"
                onClick={() => copyToClipboard(passwordData?.username || '', 'username')}
                disabled={!passwordData?.username}
                className="copy-btn"
              >
                {copyFeedback.username ? '✅ 복사됨' : '📋 복사'}
              </Button>
            </div>
          </div>

          {/* 패스워드 */}
          <div className="info-row password-row">
            <label>패스워드</label>
            <div className="info-content">
              <div className="password-display">
                <span className="info-value password-value">
                  {showPassword ? (passwordData?.password || '-') : '••••••••••••'}
                </span>
                <Button
                  variant="outline"
                  size="sm"
                  onClick={togglePasswordVisibility}
                  className="toggle-btn"
                  title={showPassword ? "패스워드 숨기기" : "패스워드 보기"}
                >
                  {showPassword ? '🙈' : '👁️'}
                </Button>
              </div>
              <Button
                variant="primary"
                size="sm"
                onClick={() => copyToClipboard(passwordData?.password || '', 'password')}
                disabled={!passwordData?.password}
                className="copy-btn primary"
              >
                {copyFeedback.password ? '✅ 복사됨' : '📋 복사'}
              </Button>
            </div>
          </div>

          {/* 웹사이트 */}
          {passwordData?.website && (
            <div className="info-row">
              <label>웹사이트</label>
              <div className="info-content">
                <a 
                  href={passwordData.website} 
                  target="_blank" 
                  rel="noopener noreferrer"
                  className="info-value website-link"
                >
                  {passwordData.website}
                </a>
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => copyToClipboard(passwordData?.website || '', 'website')}
                  className="copy-btn"
                >
                  {copyFeedback.website ? '✅ 복사됨' : '📋 복사'}
                </Button>
              </div>
            </div>
          )}

          {/* 메모 */}
          {passwordData?.notes && (
            <div className="info-row">
              <label>메모</label>
              <div className="info-content">
                <span className="info-value notes-value">{passwordData.notes}</span>
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => copyToClipboard(passwordData?.notes || '', 'notes')}
                  className="copy-btn"
                >
                  {copyFeedback.notes ? '✅ 복사됨' : '📋 복사'}
                </Button>
              </div>
            </div>
          )}
        </div>

        {/* 공유 정보 */}
        <div className="share-info-section">
          <h3>ℹ️ 공유 정보</h3>
          
          <div className="share-details">
            <div className="share-detail-item">
              <span className="label">공유일시:</span>
              <span className="value">
                {shareInfo?.createdAt ? new Date(shareInfo.createdAt.seconds * 1000).toLocaleString('ko-KR') : '-'}
              </span>
            </div>
            
            <div className="share-detail-item">
              <span className="label">만료일시:</span>
              <span className="value">
                {shareInfo?.expiresAt ? new Date(shareInfo.expiresAt.seconds * 1000).toLocaleString('ko-KR') : '-'}
              </span>
            </div>
            
            <div className="share-detail-item">
              <span className="label">남은 사용 횟수:</span>
              <span className="value">{shareInfo?.remainingUses ?? '-'}회</span>
            </div>
          </div>
        </div>

        {/* 보안 안내 */}
        <div className="security-notice">
          <h4>🛡️ 보안 안내</h4>
          <ul>
            <li>이 패스워드 정보는 일회성으로 공유된 것입니다</li>
            <li>필요한 정보를 복사한 후 즉시 이 페이지를 닫으세요</li>
            <li>공유 링크는 제한된 횟수만 사용할 수 있습니다</li>
            <li>의심스러운 접근이나 보안 문제가 있다면 즉시 패스워드를 변경하세요</li>
          </ul>
        </div>

        {/* 하단 버튼 */}
        <div className="shared-password-actions">
          <Button
            variant="secondary"
            size="md"
            onClick={handleCloseWindow}
          >
            창 닫기
          </Button>
          <Button
            variant="primary"
            size="md"
            onClick={() => navigate('/')}
          >
            패스워드 매니저 이용하기
          </Button>
        </div>
      </div>
    </div>
  );
}

export default SharedPassword;